import { Grid, Box, MenuItem, Divider } from '@mui/material';
import { earlySeasonFungicide } from '@terraframing-suite/rx-config';
import { range, find } from 'lodash-es';
import { useEffect, useCallback } from 'react';
import { Checkbox, NumericInput, Select } from '../form-controls.jsx';
import { InputsHeader } from '../InputsHeader.jsx';
import { useContext } from '../context.jsx';
import { usePermission } from '../../../hooks/use-permission.js';

export function FungicideInputs() {
  const { getParam, updateParam } = useContext();
  const canRunOther = usePermission('FUNGICIDE_RUN_OTHER');

  useEffect(() => {
    if (!getParam('earlySeasonFungicide.operatingSpeed')) {
      updateParam('earlySeasonFungicide.operatingSpeed', 14);
    }

    if (!getParam('earlySeasonFungicide.boomWidth')) {
      updateParam('earlySeasonFungicide.boomWidth', 120);
    }
  }, [getParam, updateParam]);

  const toggleProduct = useCallback(
    (product) => {
      const selectedProducts = {
        ...(getParam('earlySeasonFungicide.products') || {}),
      };
      const isSelected = selectedProducts[product];

      if (isSelected) {
        delete selectedProducts[product];
      } else {
        selectedProducts[product] = {
          ...find(earlySeasonFungicide.products, { id: product }),
        };
      }

      updateParam('earlySeasonFungicide.products', selectedProducts);
    },
    [getParam, updateParam],
  );

  return (
    <>
      {getProducts()}
      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Select
            name="earlySeasonFungicide.operatingSpeed"
            label="Operating Speed (MPH)"
            options={range(10, 19).map((increment) => (
              <MenuItem key={increment} value={increment}>
                {increment}
              </MenuItem>
            ))}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <Select
            name="earlySeasonFungicide.boomWidth"
            label="Boom Width (FT)"
            options={range(40, 160, 10).map((increment) => (
              <MenuItem key={increment} value={increment}>
                {increment}
              </MenuItem>
            ))}
            required
          />
        </Grid>
      </Grid>

      {getProductDetails()}
    </>
  );

  function getProducts() {
    return (
      <>
        <InputsHeader
          subtitle={'Select Product Utilized'}
          name={'earlySeasonFungicide.products'}
          required
        />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          {earlySeasonFungicide.products
            .filter((product) => product.id !== 'other' || canRunOther)
            .map((product, i) => (
              <Box key={i} sx={{ display: 'flex' }}>
                <Checkbox
                  name={`earlySeasonFungicide.products`}
                  value={product.id}
                  label={product.label}
                  checked={Boolean(
                    getParam(`earlySeasonFungicide.products.${product.id}`),
                  )}
                  onChange={() => toggleProduct(product.id)}
                />
              </Box>
            ))}
        </Box>
      </>
    );
  }

  function getProductDetails() {
    const selectedProducts = getParam('earlySeasonFungicide.products') || {};

    const products = Object.keys(selectedProducts);
    if (!products.length) return null;

    return (
      <Box sx={{ mt: 2 }}>
        {products.map((product) => {
          const selectedProduct = selectedProducts[product];
          const isOtherProduct = selectedProduct.id === 'other';
          const name = `earlySeasonFungicide.products.${product}`;

          return (
            <>
              <Divider sx={{ mt: 1, mb: 1 }} />
              <InputsHeader title={selectedProduct.label} />

              <Grid container xs={12} sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', flex: 1, gap: 2 }}>
                  <Grid item xs={6}>
                    <NumericInput
                      name={`${name}.baseRate`}
                      label="Product Base Rate (oz)"
                      required
                      allowDecimals
                      disabled={!isOtherProduct}
                      onChange={(e) => {
                        updateParam(e.target.name, e.target.value);

                        updateParam(
                          `${name}.maxApplicationRate`,
                          e.target.value * 2,
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumericInput
                      name={`${name}.maxApplicationRate`}
                      label="Max Application Rate (oz)"
                      allowDecimals
                      required
                    />
                  </Grid>
                </Box>
              </Grid>

              {getTrademark(selectedProduct)}
            </>
          );
        })}
      </Box>
    );
  }
}

function getTrademark(selectedProduct) {
  return (
    selectedProduct.company && (
      <Box sx={{ mt: 1 }}>
        <InputsHeader
          subtitle={`${selectedProduct.label} is a registered trademark of ${selectedProduct.company}`}
        />
      </Box>
    )
  );
}
